import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Mail, Plus, Trash2, AlertCircle } from 'lucide-react';
import {
  fetchEmailRecipients,
  createEmailRecipient,
  deleteEmailRecipient,
  clearError,
} from '../../store/slices/emailRecipientsSlice';
import './EmailConfig.css';

const EmailConfig = () => {
  const dispatch = useDispatch();
  const { recipients, loading, error } = useSelector((state) => state.emailRecipients);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ email: '', name: '' });
  const [formError, setFormError] = useState('');

  useEffect(() => {
    dispatch(fetchEmailRecipients());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormError('');
  };

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email.trim()) {
      setFormError('El email es requerido');
      return;
    }

    if (!validateEmail(formData.email)) {
      setFormError('Formato de email inválido');
      return;
    }

    try {
      await dispatch(createEmailRecipient(formData)).unwrap();
      setFormData({ email: '', name: '' });
      setShowForm(false);
      setFormError('');
    } catch (err) {
      setFormError(err || 'Error al crear destinatario');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Está seguro de eliminar este destinatario?')) {
      try {
        await dispatch(deleteEmailRecipient(id)).unwrap();
      } catch (err) {
        console.error('Error deleting recipient:', err);
      }
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setFormData({ email: '', name: '' });
    setFormError('');
  };

  return (
    <div className="email-config-container">
      <div className="email-config-header">
        <div className="header-title">
          <Mail size={24} />
          <h1>Configuración de Correos</h1>
        </div>
        <p className="header-subtitle">
          Gestiona los destinatarios adicionales que recibirán copias de los correos de preparativos
        </p>
      </div>

      {error && (
        <div className="email-config-alert email-config-alert-error">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      <div className="email-config-content">
        <div className="email-config-actions">
          <button
            className="btn-add-recipient"
            onClick={() => setShowForm(true)}
            disabled={showForm}
          >
            <Plus size={20} />
            Agregar Destinatario
          </button>
        </div>

        {showForm && (
          <div className="email-config-form-card">
            <h3>Nuevo Destinatario Adicional</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="ejemplo@correo.com"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="name">Nombre (opcional)</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Nombre del destinatario"
                />
              </div>

              {formError && (
                <div className="email-config-alert email-config-alert-error">
                  <AlertCircle size={16} />
                  <span>{formError}</span>
                </div>
              )}

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={handleCancel}>
                  Cancelar
                </button>
                <button type="submit" className="btn-submit" disabled={loading}>
                  {loading ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="email-config-list">
          <h3>Destinatarios Configurados</h3>
          
          {loading && recipients.length === 0 ? (
            <div className="email-config-loading">Cargando...</div>
          ) : recipients.length === 0 ? (
            <div className="email-config-empty">
              <Mail size={48} />
              <p>No hay destinatarios adicionales configurados</p>
              <p className="empty-subtitle">
                Agrega un destinatario para que reciba copias de los correos de preparativos
              </p>
            </div>
          ) : (
            <div className="recipients-table">
              <table>
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Nombre</th>
                    <th>Estado</th>
                    <th>Fecha Creación</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {recipients.map((recipient) => (
                    <tr key={recipient.id}>
                      <td>{recipient.email}</td>
                      <td>{recipient.name || '-'}</td>
                      <td>
                        <span className={`status-badge ${recipient.active ? 'active' : 'inactive'}`}>
                          {recipient.active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td>{new Date(recipient.created_at).toLocaleDateString('es-CL')}</td>
                      <td>
                        <button
                          className="btn-delete"
                          onClick={() => handleDelete(recipient.id)}
                          title="Eliminar destinatario"
                        >
                          <Trash2 size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="email-config-info">
          <AlertCircle size={20} />
          <div>
            <strong>Nota:</strong> El destinatario adicional recibirá copias de todos los correos enviados desde:
            <ul>
              <li>Paso 1: Solicitud de Embarque (Agente de carga)</li>
              <li>Paso 4: Coordinar Transporte (Empresa de transporte)</li>
              <li>Paso 6: Solicitud DUS (Agente de Aduana)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailConfig;
